import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ShieldCheck,
    ArrowRight,
    CheckCircle2,
    ClipboardCheck,
    Handshake,
    Lock,
    FileText,
    Receipt,
    Clock,
    Eye,
    Quote,
    Menu,
    X,
    Star,
    Smartphone,
    Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import GoogleSignInButton from "../components/sinpe/GoogleSignInButton";
import { toast } from "sonner";

// Native Google OAuth2 — no third-party redirect, no Emergent branding.
// The actual sign-in button is rendered by <GoogleSignInButton/>. The
// `handleLogin` below is a legacy fallback used by older callsites; new
// usages should render the GoogleSignInButton component directly.
const handleLogin = () => {
    // Scroll to the hero / first signin button if present
    const target = document.querySelector('[data-testid="google-signin-button"]');
    if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
};

// Brand palette (scoped to landing only)
const BRAND = {
    primary: "#1E3A8A",       // deep professional blue
    primaryDark: "#0F172A",   // near-black slate (headlines)
    primarySoft: "#1E40AF",   // hover blue
    accent: "#0D9488",        // teal accent
    accentLight: "#14B8A6",
    bgSurface: "#F8FAFC",
    border: "#E2E8F0",
    textBody: "#475569",
    textMuted: "#64748B",
};

const NAV_LINKS = [
    { href: "#solucion", label: "Nuestra Solución" },
    { href: "#como-funciona", label: "Cómo Funciona" },
    { href: "#precios", label: "Precios" },
];

// ---------------------------------------------------------------
// NAVBAR
// ---------------------------------------------------------------
function Navbar({ user }) {
    const [open, setOpen] = useState(false);
    return (
        <header className="sticky top-0 z-50 w-full bg-white/85 backdrop-blur-xl border-b border-slate-200" data-testid="landing-navbar">
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
                    <div className="h-9 w-9 rounded-xl flex items-center justify-center shadow-[0_4px_14px_rgba(30,58,138,0.25)] group-hover:scale-105 transition-transform" style={{ background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.accent} 100%)` }}>
                        <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.2} />
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className="font-display font-semibold text-slate-900 text-base tracking-tight">SINPE-Flow</span>
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">Pagos para Pymes</span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors rounded-full hover:bg-slate-50"
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-2">
                    {user ? (
                        <Link
                            to="/dashboard"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white shadow-[0_4px_14px_rgba(30,58,138,0.3)] hover:shadow-[0_6px_20px_rgba(30,58,138,0.4)] hover:-translate-y-0.5 transition-all"
                            style={{ background: BRAND.primary }}
                            data-testid="nav-dashboard-btn"
                        >
                            Ir al dashboard <ArrowRight className="w-4 h-4" />
                        </Link>
                    ) : (
                        <GoogleSignInButton size="medium" theme="filled_blue" text="signin_with" />
                    )}
                </div>

                <button
                    className="md:hidden p-2 rounded-lg hover:bg-slate-100"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Menú"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-2">
                        {NAV_LINKS.map((l) => (
                            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-medium text-slate-700 rounded-lg hover:bg-slate-50">
                                {l.label}
                            </a>
                        ))}
                        <div className="mt-2 flex justify-center">
                            <GoogleSignInButton size="large" theme="filled_blue" text="signin_with" />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

// ---------------------------------------------------------------
// HERO
// ---------------------------------------------------------------
function Hero() {
    return (
        <section className="relative overflow-hidden" data-testid="hero-section">
            <div className="absolute inset-0 -z-10">
                <div className="absolute -top-40 -right-32 h-[500px] w-[500px] rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #1E40AF 0%, transparent 60%)" }} />
                <div className="absolute top-60 -left-40 h-[400px] w-[400px] rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, #0D9488 0%, transparent 60%)" }} />
                <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, rgba(15, 23, 42, 0.04) 1px, transparent 0)`,
                    backgroundSize: "32px 32px",
                }} />
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <motion.div
                    className="lg:col-span-7"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wide mb-6 border" style={{ background: "#EFF6FF", borderColor: "#BFDBFE", color: BRAND.primary }} data-testid="hero-badge">
                        <Sparkles className="w-3.5 h-3.5" />
                        Verificá tus pagos SINPE sin sustos
                    </div>

                    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.02] text-slate-900" data-testid="hero-title">
                        Gestión <span className="relative inline-block">
                            <span className="relative z-10">Inteligente</span>
                            <span className="absolute bottom-2 left-0 right-0 h-3 -z-0 rounded" style={{ background: `${BRAND.accent}33` }} />
                        </span> de Pagos SINPE para Pymes.
                    </h1>

                    <p className="mt-7 text-xl text-slate-600 leading-relaxed max-w-2xl" data-testid="hero-subtitle">
                        Elimina estafas, organiza tu contabilidad y agiliza tus cobros en Costa Rica.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-3.5 items-start sm:items-center">
                        <GoogleSignInButton size="large" theme="filled_blue" text="signup_with" />
                        <a
                            href="#como-funciona"
                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-medium text-slate-900 bg-white border border-slate-300 hover:border-slate-900 transition-all"
                            data-testid="hero-cta-secondary"
                        >
                            Ver cómo funciona
                        </a>
                    </div>

                    <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-slate-500">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" style={{ color: BRAND.accent }} />
                            Sin tarjeta de crédito
                        </div>
                        <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4" style={{ color: BRAND.primary }} />
                            Cumple Código 06 BCCR
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" style={{ color: BRAND.primary }} />
                            Listo en 2 minutos
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="lg:col-span-5 relative"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
                >
                    <HeroDashboardMock />
                </motion.div>
            </div>
        </section>
    );
}

// CSS-rendered dashboard mockup (no stock photos)
function HeroDashboardMock() {
    return (
        <div className="relative">
            <div className="relative rounded-2xl bg-white border border-slate-200 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.18)] overflow-hidden" data-testid="hero-dashboard-mock">
                {/* Window chrome */}
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <span className="ml-3 font-mono text-[11px] text-slate-500">sinpeflow.com/dashboard</span>
                </div>

                <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-slate-500">Bienvenido</p>
                            <p className="font-display text-lg font-semibold text-slate-900">Tienda Pura Vida</p>
                        </div>
                        <div className="h-8 w-8 rounded-full" style={{ background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.accent})` }} />
                    </div>

                    <div className="grid grid-cols-3 gap-2.5 mb-4">
                        {[
                            { label: "Aprobados hoy", value: "12", c: BRAND.accent },
                            { label: "Pendiente", value: "3", c: "#D97706" },
                            { label: "Monto", value: "₡84k", c: BRAND.primary },
                        ].map((s, i) => (
                            <div key={i} className="rounded-xl border border-slate-200 p-3">
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">{s.label}</p>
                                <p className="font-display text-lg font-semibold mt-0.5" style={{ color: s.c }}>{s.value}</p>
                            </div>
                        ))}
                    </div>

                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                            <span className="text-xs font-medium text-slate-900">Pagos del día</span>
                            <span className="text-[10px] text-slate-500">3 ítems</span>
                        </div>
                        {[
                            { ref: "928374651", name: "María P.", amt: "₡25,400", st: "Aprobado", c: "#065F46", bg: "#D1FAE5" },
                            { ref: "440293812", name: "José R.", amt: "₡8,500", st: "Pendiente de revisión", c: "#92400E", bg: "#FEF3C7" },
                            { ref: "918273645", name: "Ana V.", amt: "₡12,000", st: "Aprobado", c: "#065F46", bg: "#D1FAE5" },
                        ].map((r, i) => (
                            <div key={i} className="px-3 py-2.5 flex items-center justify-between border-b border-slate-100 last:border-0">
                                <div>
                                    <p className="font-mono text-xs text-slate-900">{r.ref}</p>
                                    <p className="text-[10px] text-slate-500">{r.name}</p>
                                </div>
                                <p className="font-mono text-sm font-medium text-slate-900">{r.amt}</p>
                                <span className="text-[10px] font-medium px-2 py-1 rounded-full" style={{ color: r.c, background: r.bg }}>{r.st}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Floating phone mockup */}
            <motion.div
                className="absolute -bottom-8 -left-8 md:-left-14 w-[180px] hidden sm:block"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 }}
            >
                <div className="rounded-3xl border-[6px] border-slate-900 bg-white shadow-[0_20px_50px_-10px_rgba(15,23,42,0.4)] overflow-hidden">
                    <div className="px-3 py-2 flex items-center justify-between text-[9px] text-slate-500 bg-slate-50 border-b border-slate-200">
                        <span className="font-medium">9:41</span>
                        <span>•••</span>
                    </div>
                    <div className="p-3">
                        <p className="text-[9px] uppercase tracking-widest text-slate-500">Aprobar pago</p>
                        <p className="font-display text-base font-semibold text-slate-900 mt-1">₡25,400</p>
                        <p className="text-[10px] text-slate-500 font-mono">REF · 928374651</p>
                        <div className="mt-3 rounded-lg bg-slate-100 h-16 flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-slate-400" />
                        </div>
                        <button className="mt-3 w-full text-[10px] font-medium text-white py-2 rounded-full" style={{ background: BRAND.accent }}>
                            Aprobar
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* Floating badge */}
            <motion.div
                className="absolute -top-4 -right-4 px-3.5 py-2 rounded-full bg-white border border-slate-200 shadow-lg flex items-center gap-2"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
            >
                <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: BRAND.accent }} />
                <p className="text-xs font-medium text-slate-900">100% legitimidad</p>
            </motion.div>
        </div>
    );
}

// ---------------------------------------------------------------
// THREE PILLARS
// ---------------------------------------------------------------
function Pillars() {
    const items = [
        {
            id: "antiestafas",
            icon: ShieldCheck,
            tag: "Escudo Antiestafas",
            title: "Verificá la legitimidad de cada pago",
            desc: "Subí el comprobante, validalo contra tu cuenta y aprobalo solo cuando estés 100% seguro. Cero capturas falsas, cero pérdidas por fraude.",
            color: BRAND.primary,
            bg: "#EFF6FF",
        },
        {
            id: "auditoria",
            icon: ClipboardCheck,
            tag: "Auditoría Contable",
            title: "Conciliá pagos con facturas en segundos",
            desc: "Asigná número de factura, exportá tu historial a CSV y mantenete al día con el Código 06 del Banco Central. Tu contador te lo va a agradecer.",
            color: BRAND.accent,
            bg: "#ECFDF5",
        },
        {
            id: "delegacion",
            icon: Handshake,
            tag: "Delegación Segura",
            title: "Tus empleados gestionan, vos controlás",
            desc: "Que tu equipo apruebe pagos sin tocar tu cuenta bancaria. Roles, permisos y bitácora completa para que nada se te escape.",
            color: BRAND.primary,
            bg: "#F0FDFA",
        },
    ];

    return (
        <section id="solucion" className="py-20 md:py-28 bg-white" data-testid="pillars-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl mb-14">
                    <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: BRAND.accent }}>Nuestra solución</p>
                    <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                        Tres pilares para que tu negocio cobre por SINPE sin sustos.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {items.map((it, idx) => {
                        const Icon = it.icon;
                        return (
                            <motion.div
                                key={it.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                                className="rounded-2xl border border-slate-200 bg-white p-7 hover:shadow-[0_12px_40px_-12px_rgba(15,23,42,0.15)] transition-shadow"
                                data-testid={`pillar-${it.id}`}
                            >
                                <div className="h-14 w-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: it.bg }}>
                                    <Icon className="w-6 h-6" style={{ color: it.color }} strokeWidth={2} />
                                </div>
                                <p className="text-xs uppercase tracking-widest font-medium mb-2.5" style={{ color: it.color }}>{it.tag}</p>
                                <h3 className="font-display text-xl font-medium text-slate-900 leading-snug mb-3">{it.title}</h3>
                                <p className="text-base text-slate-600 leading-relaxed">{it.desc}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// HOW IT WORKS / PLATFORM SHOWCASE
// ---------------------------------------------------------------
function PlatformShowcase() {
    return (
        <section id="como-funciona" className="py-20 md:py-28 relative overflow-hidden" style={{ background: BRAND.bgSurface }} data-testid="showcase-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <div className="max-w-3xl mb-14">
                    <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: BRAND.accent }}>Cómo funciona</p>
                    <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                        Una plataforma diseñada para pymes ticas. En desktop y celular.
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    {/* Desktop mockup */}
                    <motion.div
                        className="lg:col-span-8"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <DesktopShowcaseMock />
                    </motion.div>

                    {/* Side: steps */}
                    <motion.div
                        className="lg:col-span-4"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <div className="space-y-6">
                            {[
                                { n: "01", t: "Tu cliente paga por SINPE", d: "Le compartís tu link de pago personalizado." },
                                { n: "02", t: "Ingresa referencia y comprobante", d: "El cliente sube la captura del SINPE Móvil." },
                                { n: "03", t: "Vos aprobás desde el dashboard", d: "Validás visualmente y marcás como Aprobado." },
                                { n: "04", t: "Exportás para tu contador", d: "CSV con número de factura, lista para conciliar." },
                            ].map((s, i) => (
                                <div key={i} className="flex gap-4" data-testid={`step-${i}`}>
                                    <div className="flex-shrink-0">
                                        <div className="h-10 w-10 rounded-full flex items-center justify-center font-mono text-xs font-semibold text-white" style={{ background: BRAND.primary }}>
                                            {s.n}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="font-display text-lg font-medium text-slate-900">{s.t}</p>
                                        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function DesktopShowcaseMock() {
    return (
        <div className="relative">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.2)] overflow-hidden" data-testid="desktop-mock">
                <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <span className="ml-4 px-3 py-0.5 rounded-md bg-white border border-slate-200 font-mono text-[11px] text-slate-500">sinpeflow.com/dashboard</span>
                </div>
                <div className="grid grid-cols-12 min-h-[380px]">
                    {/* Sidebar */}
                    <div className="col-span-3 border-r border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.accent})` }}>
                                <ShieldCheck className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="font-display font-semibold text-sm text-slate-900">SINPE-Flow</span>
                        </div>
                        <div className="space-y-1">
                            {["Dashboard", "Transacciones", "Comprobantes", "Reportes", "Equipo"].map((m, i) => (
                                <div key={i} className={`px-3 py-2 rounded-lg text-xs font-medium ${i === 0 ? "text-white" : "text-slate-600"}`} style={i === 0 ? { background: BRAND.primary } : {}}>
                                    {m}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Main */}
                    <div className="col-span-9 p-5">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-slate-500">Hoy · 21 may 2026</p>
                                <p className="font-display text-xl font-semibold text-slate-900">Transacciones</p>
                            </div>
                            <div className="flex gap-2">
                                <div className="px-3 py-1.5 rounded-full text-xs font-medium text-white" style={{ background: BRAND.primary }}>Hoy</div>
                                <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-slate-200 text-slate-600">Semana</div>
                                <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-slate-200 text-slate-600">Mes</div>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-4 gap-2.5 mb-5">
                            {[
                                { l: "Aprobados hoy", v: "12", c: BRAND.accent },
                                { l: "Pendiente revisión", v: "3", c: "#D97706" },
                                { l: "Monto del día", v: "₡84.5k", c: BRAND.primary },
                                { l: "Total mes", v: "₡1.2M", c: "#0F172A" },
                            ].map((s, i) => (
                                <div key={i} className="rounded-xl border border-slate-200 p-3 bg-white">
                                    <p className="text-[9px] uppercase tracking-wider text-slate-500">{s.l}</p>
                                    <p className="font-display text-lg font-semibold mt-1" style={{ color: s.c }}>{s.v}</p>
                                </div>
                            ))}
                        </div>

                        {/* Mini chart */}
                        <div className="rounded-xl border border-slate-200 p-3.5 mb-4 bg-white">
                            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Cobros · últimos 7 días</p>
                            <div className="flex items-end gap-2 h-16">
                                {[40, 65, 30, 80, 55, 95, 70].map((h, i) => (
                                    <div key={i} className="flex-1 rounded-t-md" style={{ height: `${h}%`, background: i === 5 ? BRAND.accent : `${BRAND.primary}cc` }} />
                                ))}
                            </div>
                        </div>

                        {/* Table */}
                        <div className="rounded-xl border border-slate-200 overflow-hidden">
                            {[
                                { ref: "928374651", name: "María Pérez", amt: "₡25,400", st: "Aprobado", c: "#065F46", bg: "#D1FAE5", hasFile: true },
                                { ref: "440293812", name: "José Ramírez", amt: "₡8,500", st: "Pendiente de revisión", c: "#92400E", bg: "#FEF3C7", hasFile: true },
                                { ref: "918273645", name: "Ana Vega", amt: "₡12,000", st: "Aprobado", c: "#065F46", bg: "#D1FAE5", hasFile: false },
                            ].map((r, i) => (
                                <div key={i} className="px-3 py-2.5 flex items-center gap-4 border-b border-slate-100 last:border-0 text-xs">
                                    <div className="font-mono text-slate-700 w-24">{r.ref}</div>
                                    <div className="text-slate-700 flex-1">{r.name}</div>
                                    <div className="font-mono font-medium text-slate-900 w-20 text-right">{r.amt}</div>
                                    <div className="w-44 flex justify-end">
                                        <span className="text-[10px] font-medium px-2 py-1 rounded-full" style={{ color: r.c, background: r.bg }}>{r.st}</span>
                                    </div>
                                    <div className="w-5">
                                        {r.hasFile && <Eye className="w-3.5 h-3.5 text-slate-400" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating comprobante card */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="absolute -bottom-8 -right-4 md:right-6 w-[220px] rounded-2xl bg-white border border-slate-200 shadow-[0_20px_40px_-10px_rgba(15,23,42,0.3)] p-4 hidden sm:block"
            >
                <p className="text-[10px] uppercase tracking-widest text-slate-500">Subir comprobante</p>
                <p className="font-display text-sm font-medium text-slate-900 mt-1">Captura del SINPE</p>
                <div className="mt-3 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1.5">
                    <FileText className="w-6 h-6 text-slate-400" />
                    <p className="text-[10px] text-slate-500">comprobante.jpg</p>
                </div>
                <button className="mt-3 w-full text-[11px] font-medium text-white py-2 rounded-full" style={{ background: BRAND.accent }}>
                    Adjuntar archivo
                </button>
            </motion.div>
        </div>
    );
}

// ---------------------------------------------------------------
// PRICING
// ---------------------------------------------------------------
// Onvo Pay Checkout URLs. Configured via env vars so the user can paste
// the real checkout-session links from their Onvo Dashboard without code
// changes. Fallbacks keep the page from blowing up if env is missing.
const ONVO_CHECKOUT_PRO_100 =
    process.env.REACT_APP_ONVO_CHECKOUT_PRO_100 || "https://checkout.onvopay.com/";
const ONVO_CHECKOUT_PRO_1 =
    process.env.REACT_APP_ONVO_CHECKOUT_PRO_1 || "https://checkout.onvopay.com/";
const ONVO_CHECKOUT_PRO_2 =
    process.env.REACT_APP_ONVO_CHECKOUT_PRO_2 || "https://checkout.onvopay.com/";

function Pricing() {
    const { user } = useAuth();

    // Gate Onvo Checkout behind a Google login. Anonymous users would
    // otherwise pay in Onvo with no SINPE-Flow account to attach the
    // subscription to — the webhook would file the payment as "unmatched"
    // and the user would need manual support to claim it.
    const handleCheckoutClick = (e, checkoutUrl) => {
        if (user) return; // logged in → let the <a> open Onvo normally
        e.preventDefault();
        toast.message("Primero iniciá sesión con Google", {
            description: "Tu cuenta SINPE-Flow se activa automáticamente cuando completes el pago.",
        });
        // Remember the intended checkout URL so we can deep-link after login
        try {
            sessionStorage.setItem("sinpeflow_pending_checkout", checkoutUrl);
        } catch (err) {
            // sessionStorage can throw in private/incognito mode — user can
            // still click the CTA again after logging in, just no deep-link.
            console.warn("[pricing] sessionStorage write failed:", err);
        }
        const target = document.querySelector('[data-testid="google-signin-button"]');
        if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const plans = [
        {
            id: "prueba",
            name: "Plan Prueba",
            price: "₡0",
            period: "Gratis",
            tag: "Para probar",
            features: [
                "20 transacciones mensuales",
                "Lectura automática de comprobantes con IA",
                "Detección básica de duplicados",
                "Exportación CSV para tu contador",
            ],
            cta: "Empezar gratis",
            onClick: handleLogin,
            highlight: false,
        },
        {
            id: "emprendedor",
            name: "Plan Emprendedor",
            price: "₡5.000",
            period: "/ mes",
            tag: "Microemprendedores",
            features: [
                "Hasta 100 transacciones al mes",
                "Empleados ilimitados",
                "Alertas de duplicados en tiempo real",
                "Dashboard en vivo con sonido de caja",
                "Exportación CSV para tu contador",
            ],
            cta: user ? "Suscribirme · Emprendedor" : "Iniciá sesión para suscribirte",
            href: ONVO_CHECKOUT_PRO_100,
            highlight: false,
        },
        {
            id: "pyme-pro-1",
            name: "Pyme Pro · Nivel 1",
            price: "₡9.900",
            period: "/ mes",
            tag: "Recomendado",
            features: [
                "Hasta 300 transacciones al mes",
                "Cuentas de empleados ilimitadas",
                "Alertas de duplicados en tiempo real",
                "Dashboard en vivo con sonido de caja",
                "Reportes contables CSV (Código 06)",
                "QR de pago descargable",
                "Soporte prioritario",
            ],
            cta: user ? "Suscribirme · Nivel 1" : "Iniciá sesión para suscribirte",
            href: ONVO_CHECKOUT_PRO_1,
            highlight: true,
        },
        {
            id: "pyme-pro-2",
            name: "Pyme Pro · Nivel 2",
            price: "₡16.900",
            period: "/ mes",
            tag: "Volumen alto",
            features: [
                "Hasta 1,200 transacciones al mes",
                "Cuentas de empleados ilimitadas",
                "Alertas de duplicados en tiempo real",
                "Dashboard en vivo con sonido de caja",
                "Reportes contables CSV (Código 06)",
                "QR de pago descargable",
                "Soporte prioritario",
            ],
            cta: user ? "Suscribirme · Nivel 2" : "Iniciá sesión para suscribirte",
            href: ONVO_CHECKOUT_PRO_2,
            highlight: false,
            premium: true,
        },
    ];

    return (
        <section id="precios" className="py-20 md:py-28 bg-white" data-testid="pricing-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: BRAND.accent }}>Precios</p>
                    <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                        Cuatro planes claros. Empezás gratis.
                    </h2>
                    <p className="text-base text-slate-600 mt-4">Sin contratos. Cancelás cuando quieras. Solo pagás lo que necesitás.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
                    {plans.map((p) => (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5 }}
                            className={`relative rounded-2xl p-8 flex flex-col ${
                                p.highlight
                                    ? "shadow-[0_24px_48px_-20px_rgba(30,58,138,0.45)] border-2"
                                    : p.premium
                                    ? "border border-slate-300 bg-slate-50"
                                    : "border border-slate-200 bg-white"
                            }`}
                            style={p.highlight ? { background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)", borderColor: BRAND.primary } : {}}
                            data-testid={`plan-${p.id}`}
                        >
                            {p.highlight && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-medium text-white shadow-md whitespace-nowrap" style={{ background: BRAND.primary }}>
                                    {p.tag}
                                </div>
                            )}

                            {!p.highlight && (
                                <p
                                    className="text-xs uppercase tracking-widest font-medium mb-2"
                                    style={{ color: p.premium ? BRAND.accent : "#64748B" }}
                                >
                                    {p.tag}
                                </p>
                            )}

                            <h3 className="font-display text-2xl font-semibold text-slate-900 mt-2">{p.name}</h3>
                            <div className="mt-4 flex items-baseline gap-1.5">
                                <span className="font-display text-5xl font-semibold tracking-tighter text-slate-900">{p.price}</span>
                                <span className="text-sm text-slate-500">{p.period}</span>
                            </div>

                            <ul className="mt-7 space-y-3 flex-1">
                                {p.features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: p.highlight ? BRAND.primary : BRAND.accent }} />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>

                            {p.href ? (
                                <a
                                    href={p.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => handleCheckoutClick(e, p.href)}
                                    className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all hover:-translate-y-0.5 ${
                                        p.highlight
                                            ? "text-white shadow-[0_6px_20px_rgba(30,58,138,0.3)]"
                                            : "text-white shadow-[0_6px_20px_rgba(13,148,136,0.3)]"
                                    }`}
                                    style={{ background: p.highlight ? BRAND.primary : BRAND.accent }}
                                    data-testid={`plan-${p.id}-cta`}
                                >
                                    {p.cta} <ArrowRight className="w-4 h-4" />
                                </a>
                            ) : (
                                <button
                                    onClick={p.onClick}
                                    className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-white border border-slate-300 text-slate-900 hover:border-slate-900 transition-all"
                                    data-testid={`plan-${p.id}-cta`}
                                >
                                    {p.cta}
                                </button>
                            )}
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// TESTIMONIALS
// ---------------------------------------------------------------
function Testimonials() {
    const testimonials = [
        {
            quote: "Dejé de revisar comprobantes WhatsApp uno por uno. Ahora aprobamos pagos en segundos y mi contadora exporta todo a fin de mes.",
            name: "Karina Solís",
            role: "Dueña, Repostería Doña Cris",
            img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=160&h=160&fit=crop&crop=face",
        },
        {
            quote: "Mis cajeros aprueban los SINPE sin que les pase mi clave bancaria. Para mí eso solo ya vale el precio del plan.",
            name: "Mauricio Vargas",
            role: "Gerente, Soda Pura Vida",
            img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop&crop=face",
        },
        {
            quote: "Me ahorré dos estafas el primer mes. La gente ya no se atreve a mandarme comprobantes editados, porque exijo el link.",
            name: "Andrea Mora",
            role: "Boutique Mora · Heredia",
            img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=160&h=160&fit=crop&crop=face",
        },
    ];

    return (
        <section className="py-20 md:py-28" style={{ background: BRAND.bgSurface }} data-testid="testimonials-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: BRAND.accent }}>Testimonios</p>
                    <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                        Pymes ticas que ya cobran tranquilas.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {testimonials.map((t, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="rounded-2xl bg-white border border-slate-200 p-7 flex flex-col"
                            data-testid={`testimonial-${i}`}
                        >
                            <Quote className="w-7 h-7 mb-4" style={{ color: BRAND.accent }} />
                            <p className="text-base text-slate-700 leading-relaxed flex-1">"{t.quote}"</p>
                            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-slate-100">
                                <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                                <div>
                                    <p className="font-display font-medium text-slate-900 text-sm">{t.name}</p>
                                    <p className="text-xs text-slate-500">{t.role}</p>
                                </div>
                                <div className="ml-auto flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Star key={s} className="w-3 h-3 fill-amber-400 text-amber-400" />
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// FOOTER
// ---------------------------------------------------------------
function LandingFooter() {
    return (
        <footer className="bg-slate-900 text-white" data-testid="landing-footer">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-2.5">
                            <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.accent})` }}>
                                <ShieldCheck className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-display font-semibold text-lg">SINPE-Flow</span>
                        </div>
                        <p className="text-sm text-slate-400 mt-5 max-w-sm leading-relaxed">
                            La forma inteligente de gestionar tus cobros por SINPE Móvil. Antiestafas, conciliación contable y delegación segura para pymes en Costa Rica.
                        </p>
                    </div>

                    <div className="md:col-span-2">
                        <p className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-4">Producto</p>
                        <ul className="space-y-2.5 text-sm text-slate-300">
                            <li><a href="#solucion" className="hover:text-white transition-colors">Solución</a></li>
                            <li><a href="#como-funciona" className="hover:text-white transition-colors">Cómo funciona</a></li>
                            <li><a href="#precios" className="hover:text-white transition-colors">Precios</a></li>
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <p className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-4">Legal</p>
                        <ul className="space-y-2.5 text-sm text-slate-300">
                            <li><Link to="/terminos" className="hover:text-white transition-colors" data-testid="footer-terms">Términos</Link></li>
                            <li><Link to="/terminos" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacidad</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <p className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-4">Contacto</p>
                        <ul className="space-y-2.5 text-sm text-slate-300">
                            <li className="font-mono">hola@sinpeflow.com</li>
                            <li>San José, Costa Rica</li>
                            <li>
                                <button
                                    onClick={handleLogin}
                                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium text-white"
                                    style={{ background: BRAND.accent }}
                                >
                                    Probar gratis <ArrowRight className="w-3 h-3" />
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-400">
                    <p>&copy; {new Date().getFullYear()} SINPE-Flow. Todos los derechos reservados.</p>
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800">
                            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: BRAND.accentLight }} />
                            <span className="text-slate-300">Hecho en Costa Rica</span>
                            <span className="text-base leading-none">🇨🇷</span>
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}

// ---------------------------------------------------------------
// MAIN
// ---------------------------------------------------------------
export default function Landing() {
    const { user } = useAuth();

    return (
        <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
            <Navbar user={user} />
            <Hero />
            <Pillars />
            <PlatformShowcase />
            <Pricing />
            <Testimonials />

            {/* Final CTA */}
            <section className="py-20 md:py-24" data-testid="final-cta-section">
                <div className="max-w-4xl mx-auto px-6 md:px-10">
                    <div className="rounded-3xl p-10 md:p-14 text-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)` }}>
                        <div className="absolute inset-0 opacity-20" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.3) 1px, transparent 0)`,
                            backgroundSize: "24px 24px",
                        }} />
                        <div className="relative">
                            <Smartphone className="w-12 h-12 mx-auto mb-6 text-white opacity-80" strokeWidth={1.5} />
                            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tighter text-white leading-tight">
                                Cobrá por SINPE sin la incertidumbre.
                            </h2>
                            <p className="mt-5 text-lg text-white/80 max-w-xl mx-auto">
                                Empezá gratis hoy. Sin tarjeta, sin compromiso.
                            </p>
                            <div className="mt-8 inline-flex justify-center" data-testid="final-cta-btn">
                                <GoogleSignInButton size="large" theme="filled_blue" text="signup_with" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
}
